import React, { useMemo, useRef, useState } from 'react';
import { X, Upload, Loader2, FileSpreadsheet } from 'lucide-react';
import { planningService } from '../../services/planningService';
import { PERIODS } from '../../utils/financialPeriods';

// =============================================================================
// Planning > Network — volume upload
//
// Accepts the two shapes a planner actually has, because a lane plan is almost
// always built in Excel before it is built here:
//
//   WIDE   origin, destination, sku, P1 … P12      (how a planner lays it out)
//   LONG   origin, destination, sku, period, qty   (how a system exports it)
//
// The shape is detected, not asked for. A `period` column plus a quantity
// column means long; anything else with trailing numeric columns means wide.
//
// Missing lanes are created by default — a file is usually the FIRST thing that
// arrives, and refusing it until someone has hand-drawn twenty edges defeats
// the point of uploading. Unknown SITE codes are still rejected: that is a typo,
// and inventing a site from a spreadsheet cell is not an upload's decision.
// =============================================================================

const ORIGIN_KEYS = ['origin', 'origin_site', 'origin_site_code', 'from', 'from_site', 'source'];
const DEST_KEYS = ['destination', 'dest', 'dest_site', 'dest_site_code', 'to', 'to_site'];
const SKU_KEYS = ['sku', 'stock_code', 'stockcode', 'item', 'item_code', 'product'];
const PERIOD_KEYS = ['period', 'financial_period', 'month', 'per'];
const QTY_KEYS = ['qty', 'quantity', 'volume', 'units', 'value'];
const UOM_KEYS = ['uom', 'unit', 'unit_of_measure'];

const norm = (s) => String(s || '').trim().toLowerCase().replace(/[\s.-]+/g, '_');
const findCol = (headers, keys) => headers.findIndex((h) => keys.includes(norm(h)));

// Tab first: a paste out of Excel is tab-separated, and a comma inside a
// description would otherwise split a column.
const splitLine = (line, delim) => line.split(delim).map((c) => c.trim().replace(/^"|"$/g, ''));

const parse = (text) => {
  const lines = String(text || '').split(/\r?\n/).filter((l) => l.trim() !== '');
  if (lines.length < 2) return { rows: [], error: 'Need a header row and at least one data row.' };

  const delim = lines[0].includes('\t') ? '\t' : ',';
  const headers = splitLine(lines[0], delim);

  const iOrigin = findCol(headers, ORIGIN_KEYS);
  const iDest = findCol(headers, DEST_KEYS);
  const iSku = findCol(headers, SKU_KEYS);
  const iPeriod = findCol(headers, PERIOD_KEYS);
  const iQty = findCol(headers, QTY_KEYS);
  const iUom = findCol(headers, UOM_KEYS);

  if (iOrigin < 0) return { rows: [], error: `No origin column. Expected one of: ${ORIGIN_KEYS.join(', ')}.` };
  if (iDest < 0) return { rows: [], error: `No destination column. Expected one of: ${DEST_KEYS.join(', ')}.` };
  if (iSku < 0) return { rows: [], error: `No SKU column. Expected one of: ${SKU_KEYS.join(', ')}.` };

  const isLong = iPeriod >= 0 && iQty >= 0;

  // Wide: everything that is not one of the named columns, in file order, is a
  // period. Twelve of them are used; fewer is allowed (a part-year plan).
  const periodCols = [];
  if (!isLong) {
    const named = new Set([iOrigin, iDest, iSku, iUom].filter((i) => i >= 0));
    headers.forEach((_, i) => { if (!named.has(i)) periodCols.push(i); });
    if (!periodCols.length) {
      return { rows: [], error: 'No period columns found. Add P1…P12, or a period + quantity column pair.' };
    }
    if (periodCols.length > 12) periodCols.length = 12;
  }

  const rows = [];
  for (let n = 1; n < lines.length; n += 1) {
    const c = splitLine(lines[n], delim);
    const origin = c[iOrigin];
    const dest = c[iDest];
    const sku = c[iSku];
    if (!origin || !dest || !sku) continue;
    const uom = iUom >= 0 ? c[iUom] : undefined;

    if (isLong) {
      const period = parseInt(c[iPeriod], 10);
      const qty = Number(String(c[iQty] || '0').replace(/[, ]/g, ''));
      if (!(period >= 1 && period <= 12)) return { rows: [], error: `Line ${n + 1}: period "${c[iPeriod]}" is not 1-12.` };
      if (Number.isNaN(qty)) return { rows: [], error: `Line ${n + 1}: quantity "${c[iQty]}" is not a number.` };
      rows.push({ origin_site_code: origin, dest_site_code: dest, stock_code: sku, financial_period: period, quantity: qty, unit_of_measure: uom });
    } else {
      const quantities = Array(12).fill(0);
      for (let p = 0; p < periodCols.length; p += 1) {
        const raw = String(c[periodCols[p]] ?? '0').replace(/[, ]/g, '');
        const qty = raw === '' ? 0 : Number(raw);
        if (Number.isNaN(qty)) return { rows: [], error: `Line ${n + 1}, column "${headers[periodCols[p]]}": "${raw}" is not a number.` };
        quantities[p] = qty;
      }
      rows.push({ origin_site_code: origin, dest_site_code: dest, stock_code: sku, quantities, unit_of_measure: uom });
    }
  }

  if (!rows.length) return { rows: [], error: 'No usable data rows — every row needs an origin, a destination and a SKU.' };
  return { rows, shape: isLong ? 'long' : 'wide', periodCount: isLong ? 12 : periodCols.length, error: '' };
};

const MODES = [
  { code: 'overwrite', label: 'Overwrite', hint: 'Replace the values in the file, leave everything else alone.' },
  { code: 'append', label: 'Add to', hint: 'Sum the file onto whatever is already there.' },
  { code: 'replace', label: 'Replace all', hint: 'Clear every lane volume in this version first.' },
];

const field = 'w-full text-xs rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 px-2 py-1.5 outline-none focus:ring-2 focus:ring-blue-500/40';

export default function UploadVolumesModal({ onClose, onDone }) {
  const [text, setText] = useState('');
  const [mode, setMode] = useState('overwrite');
  const [createLanes, setCreateLanes] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  const parsed = useMemo(() => (text.trim() ? parse(text) : { rows: [], error: '' }), [text]);
  const lanePairs = useMemo(
    () => new Set(parsed.rows.map((r) => `${r.origin_site_code} → ${r.dest_site_code}`)),
    [parsed.rows]
  );

  const pickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setText(await file.text());
  };

  const submit = async () => {
    if (!parsed.rows.length) return;
    setBusy(true);
    setError('');
    try {
      const res = await planningService.uploadVolumes(parsed.rows, { mode, createLanes });
      onDone(res.message || 'Volumes loaded.');
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Upload failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-6" onClick={onClose}>
      <div className="w-full max-w-3xl max-h-full flex flex-col rounded-xl bg-white dark:bg-slate-800 shadow-2xl overflow-hidden"
           onClick={(e) => e.stopPropagation()}>
        <div className="shrink-0 flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
          <div>
            <h2 className="m-0 text-base font-bold text-slate-900 dark:text-slate-100">Upload lane volumes</h2>
            <p className="m-0 mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              Paste from Excel or choose a CSV. Columns: origin, destination, SKU, then either P1…P12 or period + quantity.
            </p>
          </div>
          <button onClick={onClose} className="p-1 rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-4">
          <div className="flex items-center gap-2">
            <button className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
                    onClick={() => fileRef.current?.click()}>
              <FileSpreadsheet className="w-3.5 h-3.5" /> Choose CSV
            </button>
            <input ref={fileRef} type="file" accept=".csv,.tsv,.txt" className="hidden" onChange={pickFile} />
            <span className="text-xs text-slate-400">or paste below</span>
          </div>

          <textarea
            className={`${field} font-mono`}
            rows={9}
            placeholder={`origin,destination,sku,${PERIODS.map((p) => `P${p}`).join(',')}\nPLNT-01,WH-01,RBY-PUP-2KG,120,130,140,150,150,160,160,150,140,130,120,120`}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />

          {parsed.error && (
            <div className="px-3 py-2 rounded-lg border border-red-300 bg-red-50 dark:border-red-500/40 dark:bg-red-500/10">
              <p className="m-0 text-xs text-red-700 dark:text-red-300">{parsed.error}</p>
            </div>
          )}

          {parsed.rows.length > 0 && (
            <div className="px-3 py-2 rounded-lg border border-emerald-300 bg-emerald-50 dark:border-emerald-500/40 dark:bg-emerald-500/10">
              <p className="m-0 text-xs text-emerald-800 dark:text-emerald-300">
                Read <strong>{parsed.rows.length}</strong> row(s) in <strong>{parsed.shape}</strong> format
                {parsed.shape === 'wide' && parsed.periodCount < 12 && ` (${parsed.periodCount} periods — the rest default to zero)`}
                {' '}across <strong>{lanePairs.size}</strong> lane(s): {[...lanePairs].slice(0, 4).join(', ')}
                {lanePairs.size > 4 ? ` and ${lanePairs.size - 4} more` : ''}.
              </p>
            </div>
          )}

          <div>
            <p className="m-0 mb-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">How to apply it</p>
            <div className="grid grid-cols-3 gap-2">
              {MODES.map((m) => (
                <button
                  key={m.code}
                  onClick={() => setMode(m.code)}
                  className={`text-left px-3 py-2 rounded-lg border transition-colors ${mode === m.code
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/15'
                    : 'border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700'}`}
                >
                  <span className="block text-xs font-bold text-slate-800 dark:text-slate-100">{m.label}</span>
                  <span className="block mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">{m.hint}</span>
                </button>
              ))}
            </div>
          </div>

          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" className="mt-0.5" checked={createLanes} onChange={(e) => setCreateLanes(e.target.checked)} />
            <span className="text-xs text-slate-600 dark:text-slate-300">
              Create lanes that are not on the map yet
              <span className="block text-[11px] text-slate-400">
                Unknown site codes are still rejected — those are typos, not new sites.
              </span>
            </span>
          </label>

          {error && (
            <div className="px-3 py-2 rounded-lg border border-red-300 bg-red-50 dark:border-red-500/40 dark:bg-red-500/10">
              <p className="m-0 text-xs text-red-700 dark:text-red-300">{error}</p>
            </div>
          )}
        </div>

        <div className="shrink-0 flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 dark:border-slate-700">
          <button className="px-3 py-1.5 text-xs font-medium rounded-md text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700" onClick={onClose}>
            Cancel
          </button>
          <button
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
            onClick={submit}
            disabled={busy || !parsed.rows.length}
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            Load {parsed.rows.length ? `${parsed.rows.length} row(s)` : ''}
          </button>
        </div>
      </div>
    </div>
  );
}
